import { cx, text } from '../styles/theme';
import type { UsageResponse } from '../types/api';

// One hour figure in the equation. Numerals are mono + tabular so columns don't
// jitter when the range changes (#630 §3).
function Figure({ value, unit, label }: { value: string; unit: string; label: string }) {
  return (
    <div className="min-w-0">
      <div className="font-mono text-2xl font-semibold tabular-nums text-navy-900 dark:text-white">
        <span>{value}</span>
        <span className="ml-1 text-base font-normal text-navy-400">{unit}</span>
      </div>
      <div className="mt-0.5 text-xs uppercase tracking-wider text-navy-400">{label}</div>
    </div>
  );
}

/**
 * LeverageCard (#630 §1/§9) — the hero card, read as an equation:
 *   driving hours → agent hours = leverage×
 * with a delta chip against the prior period's leverage. `anyAgentRunningHours`
 * is shown as context only, labelled "any agent running", and kept clear of the
 * ratio so it is never mistaken for the denominator (§8.5).
 */
export function LeverageCard({ data }: { data: UsageResponse }) {
  const days = data.days ?? [];
  const drivingHours = days.reduce((a, d) => a + (d.drivingHours ?? 0), 0);
  const agentHours = days.reduce((a, d) => a + (d.mainHours ?? 0) + (d.subHours ?? 0), 0);
  const leverage = drivingHours > 0 ? agentHours / drivingHours : 0;

  const prior = data.priorLeverage ?? -1;
  const deltaPct = prior > 0 ? (leverage - prior) / prior : null;
  const anyRunning = data.anyAgentRunningHours ?? 0;

  const fmtHours = (h: number) => h.toFixed(1);
  const fmtSignedPct = (r: number) =>
    `${r >= 0 ? '+' : '−'}${Math.abs(Math.round(r * 100))}%`;

  return (
    <div className="gp-card gp-card--hairline">
      <div className="mb-4 flex items-center gap-2">
        <span
          aria-hidden
          className="inline-block rounded-full"
          style={{ width: 6, height: 6, background: 'var(--color-orange-500)' }}
        />
        <span className="text-xs font-semibold uppercase tracking-wider text-orange-600">
          Leverage
        </span>
      </div>

      {/* The equation: driving → agent = ratio. */}
      <div className="flex flex-wrap items-center gap-x-4 gap-y-3">
        <Figure value={fmtHours(drivingHours)} unit="h" label="driving hours" />
        <span aria-hidden className="text-xl text-navy-300">
          →
        </span>
        <Figure value={fmtHours(agentHours)} unit="h" label="agent hours" />
        <span aria-hidden className="text-xl text-navy-300">
          =
        </span>
        <div className="min-w-0">
          <div className="flex items-baseline gap-2">
            <span className="font-mono text-2xl font-semibold tabular-nums text-navy-900 dark:text-white">
              {drivingHours > 0 ? `${leverage.toFixed(1)}×` : '—'}
            </span>
            {deltaPct !== null && (
              <span
                className={cx('gp-delta', deltaPct >= 0 ? 'gp-delta--success' : 'gp-delta--error')}
              >
                {fmtSignedPct(deltaPct)}
              </span>
            )}
          </div>
          <div className="mt-0.5 text-xs uppercase tracking-wider text-navy-400">leverage</div>
        </div>
      </div>

      {/* §8.5 — context only, kept clear of the ratio above so it never reads as
          the denominator. */}
      <div className="mt-4 border-t border-gray-100 pt-3 dark:border-navy-800">
        <span className={cx('font-mono text-sm tabular-nums', text.secondary)}>
          {fmtHours(anyRunning)} h
        </span>
        <span className={cx('ml-2 text-xs', text.muted)}>any agent running</span>
      </div>
    </div>
  );
}
